// src/pages/MyData/MyData.js
import React, { useState, useEffect, useCallback } from 'react';
import { 
  Download, 
  Calendar, 
  Clock, 
  Footprints, 
  RotateCcw, 
  Target, 
  Trash2,
  Zap,        // For Springs
  RefreshCw,  // For Roll-Throughs
  ArrowDownCircle  // For Jump Landings
} from 'lucide-react';
import { dataService } from '../../storage/dataService';
import './MyData.css';

function MyData({ currentUser }) {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  const fetchSessions = useCallback(async () => {
    if (!currentUser?.id) {
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      const data = await dataService.getSessionsWithStats(currentUser.id);
      setSessions(data || []);
    } catch (err) {
      console.error('Failed to fetch sessions:', err);
      setError('Failed to load sessions. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [currentUser?.id]);

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  const handleDownload = async (session) => {
    try {
      setDownloadingId(session.id);
      await dataService.downloadCSV(session.id, session.session_name);
    } catch (err) {
      console.error('Failed to download:', err);
      alert('Failed to download session data. Please try again.');
    } finally {
      setDownloadingId(null);
    }
  };

  const handleDeleteClick = (sessionId) => {
    setConfirmDeleteId(sessionId);
  };

  const handleDeleteConfirm = async () => {
    if (!confirmDeleteId) return;
    
    try {
      setDeletingId(confirmDeleteId);
      await dataService.deleteSession(confirmDeleteId);
      
      setSessions(prev => prev.filter(s => s.id !== confirmDeleteId));
      console.log('✅ Session deleted');
    } catch (err) {
      console.error('Failed to delete session:', err);
      alert('Failed to delete session. Please try again.');
    } finally {
      setDeletingId(null);
      setConfirmDeleteId(null);
    }
  };

  const handleDeleteCancel = () => {
    setConfirmDeleteId(null);
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '0s';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    if (mins === 0) return `${secs}s`;
    return `${mins}m ${secs}s`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getAlignmentInfo = (stats) => {
    if (!stats) return { status: 'none', label: 'No Data', color: '#9E9E9E', percentage: 0 };
    
    const { goodCount = 0, okCount = 0, badCount = 0 } = stats;
    const total = goodCount + okCount + badCount;
    
    if (total === 0) {
      return { status: 'none', label: 'No Data', color: '#9E9E9E', percentage: 0 };
    }

    if (goodCount >= okCount && goodCount >= badCount) {
      return { 
        status: 'good', 
        label: 'Good', 
        color: '#4CAF50', 
        percentage: Math.round((goodCount / total) * 100) 
      };
    } else if (okCount >= goodCount && okCount >= badCount) {
      return { 
        status: 'ok', 
        label: 'OK', 
        color: '#FFC107', 
        percentage: Math.round((okCount / total) * 100) 
      };
    } else {
      return { 
        status: 'bad', 
        label: 'Needs Work', 
        color: '#F44336', 
        percentage: Math.round((badCount / total) * 100) 
      };
    }
  };

  const getFootLabel = (foot) => {
    switch (foot) {
      case 'left': return 'Left Foot';
      case 'right': return 'Right Foot';
      case 'both': return 'Both Feet';
      default: return 'Unknown';
    }
  };

  const getFootIcon = (foot) => {
    switch (foot) {
      case 'left': return '🦶';
      case 'right': return '🦶';
      case 'both': return '👣';
      default: return '❓';
    }
  };

  // Calculate total transitions for a session
  const getTotalTransitions = (stats) => {
    if (!stats) return 0;
    return (stats.rollThroughs || 0) + (stats.springs || 0) + (stats.jumpLandings || 0);
  };

  if (loading) {
    return (
      <div className="my-data-page">
        <div className="loading-state">
          <div className="spinner" />
          <p>Loading your sessions...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="my-data-page">
        <div className="error-state">
          <p>{error}</p>
          <button onClick={fetchSessions} className="retry-button">
            <RotateCcw size={16} />
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="my-data-page">
      {/* Delete Confirmation Modal */}
      {confirmDeleteId && (
        <div className="delete-modal-overlay" onClick={handleDeleteCancel}>
          <div className="delete-modal" onClick={e => e.stopPropagation()}>
            <div className="delete-modal-icon">
              <Trash2 size={48} />
            </div>
            <h3>Delete Session?</h3>
            <p>This will permanently delete this session and all its data. This action cannot be undone.</p>
            <div className="delete-modal-buttons">
              <button 
                className="cancel-btn" 
                onClick={handleDeleteCancel}
                disabled={deletingId === confirmDeleteId}
              >
                Cancel
              </button>
              <button 
                className="confirm-delete-btn" 
                onClick={handleDeleteConfirm}
                disabled={deletingId === confirmDeleteId}
              >
                {deletingId === confirmDeleteId ? (
                  <>
                    <div className="spinner-small" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 size={16} />
                    Delete
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="page-header">
        <h1>My Sessions</h1>
        <p className="subtitle">
          {sessions.length} recorded session{sessions.length !== 1 ? 's' : ''}
        </p>
      </div>

      {sessions.length === 0 ? (
        <div className="empty-state">
          <Footprints size={64} strokeWidth={1} />
          <h2>No Sessions Yet</h2>
          <p>Start a recording session to see your data here.</p>
        </div>
      ) : (
        <div className="sessions-grid">
          {sessions.map((session) => {
            const alignment = getAlignmentInfo(session.stats);
            const totalTransitions = getTotalTransitions(session.stats);
            
            return (
              <div key={session.id} className="session-card">
                {/* Header with Delete Button */}
                <div className="card-header">
                  <div className="card-header-left">
                    <h3 className="session-name">{session.session_name || 'Untitled Session'}</h3>
                    <span className="session-date">
                      <Calendar size={14} />
                      {formatDate(session.started_at)}
                    </span>
                  </div>
                  <button 
                    className="delete-btn"
                    onClick={() => handleDeleteClick(session.id)}
                    title="Delete session"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>

                {/* Foot & Time Row */}
                <div className="foot-time-row">
                  <div className="foot-indicator">
                    <span className="foot-icon">{getFootIcon(session.stats?.foot)}</span>
                    <span className="foot-label">{getFootLabel(session.stats?.foot)}</span>
                  </div>
                  
                  <div className="time-on-pointe">
                    <Clock size={18} />
                    <span className="time-value">{formatDuration(session.stats?.timeOnPointe)}</span>
                    <span className="time-label">on pointe</span>
                  </div>
                </div>

                {/* Transitions Section */}
                <div className="transitions-section">
                  <div className="section-header">
                    <span>Transitions</span>
                    <span className="total-count">{totalTransitions} total</span>
                  </div>
                  
                  <div className="transitions-grid">
                    <div className="transition-box roll-through">
                      <RefreshCw size={20} />
                      <span className="transition-value">{session.stats?.rollThroughs || 0}</span>
                      <span className="transition-label">Roll-Throughs</span>
                    </div>
                    
                    <div className="transition-box spring">
                      <Zap size={20} />
                      <span className="transition-value">{session.stats?.springs || 0}</span>
                      <span className="transition-label">Springs</span>
                    </div>
                    
                    <div className="transition-box jump-landing">
                      <ArrowDownCircle size={20} />
                      <span className="transition-value">{session.stats?.jumpLandings || 0}</span>
                      <span className="transition-label">Jump Landings</span>
                    </div>
                  </div>
                </div>

                {/* Alignment Status */}
                <div className="alignment-section">
                  <div className="alignment-header">
                    <Target size={16} />
                    <span>Overall Alignment</span>
                  </div>
                  <div className="alignment-bar-container">
                    <div 
                      className="alignment-bar"
                      style={{ 
                        width: `${alignment.percentage}%`,
                        backgroundColor: alignment.color 
                      }}
                    />
                  </div>
                  <div className="alignment-details">
                    <span 
                      className="alignment-status"
                      style={{ color: alignment.color }}
                    >
                      {alignment.label}
                    </span>
                    <span className="alignment-percentage">
                      {alignment.percentage}% of time on pointe
                    </span>
                  </div>
                </div>

                {/* Alignment Breakdown */}
                <div className="alignment-breakdown">
                  <div className="breakdown-item good">
                    <span className="dot" />
                    <span>Good: {session.stats?.goodCount || 0}</span>
                  </div>
                  <div className="breakdown-item ok">
                    <span className="dot" />
                    <span>OK: {session.stats?.okCount || 0}</span>
                  </div>
                  <div className="breakdown-item bad">
                    <span className="dot" />
                    <span>Needs Work: {session.stats?.badCount || 0}</span>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="card-actions">
                  <button 
                    className="download-button"
                    onClick={() => handleDownload(session)}
                    disabled={downloadingId === session.id}
                  >
                    {downloadingId === session.id ? (
                      <>
                        <div className="spinner-small" />
                        Downloading...
                      </>
                    ) : (
                      <>
                        <Download size={18} />
                        Download CSV
                      </>
                    )}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default MyData;